import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./AlbumsGallery.css";

const AlbumGallery = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { album } = location.state || { album: { title: "", data: [] } };

  const [show, setShow] = useState(false);
  const [modalMedia, setModalMedia] = useState(null);
  const [filter, setFilter] = useState("all"); // all | image | video | audio

  const handleShow = (media) => {
    setModalMedia(media);
    setShow(true);
  };

  const handleClose = () => {
    setModalMedia(null);
    setShow(false);
  };

  const items = Array.isArray(album?.data) ? album.data : [];
  const counts = useMemo(() => {
    const c = { all: items.length, image: 0, video: 0, audio: 0 };
    items.forEach((it) => {
      if (it?.extension_type === "image") c.image += 1;
      if (it?.extension_type === "video") c.video += 1;
      if (it?.extension_type === "audio") c.audio += 1;
    });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it?.extension_type === filter);
  }, [items, filter]);

  return (
    <motion.div
      className="ag-wrap"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 0.68, 0, 1.2] }}
    >
      <div className="ag-hero">
        <div className="ag-hero-left">
          <h1 className="ag-title">{album?.title || "Album"}</h1>
          <p className="ag-sub">
            Browse photos, videos, and audio in a clean premium gallery.
          </p>
        </div>
        <div className="ag-hero-right">
          <span className="ag-pill">
            <i className="bx bx-images"></i> {counts.all} items
          </span>
          <button className="ag-back" onClick={() => navigate(-1)} type="button">
            <i className="bx bx-arrow-back"></i> Back
          </button>
        </div>
      </div>

      <div className="ag-filters">
        <button
          className={`ag-filter ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
          type="button"
        >
          All <span>{counts.all}</span>
        </button>
        <button
          className={`ag-filter ${filter === "image" ? "active" : ""}`}
          onClick={() => setFilter("image")}
          type="button"
        >
          Photos <span>{counts.image}</span>
        </button>
        <button
          className={`ag-filter ${filter === "video" ? "active" : ""}`}
          onClick={() => setFilter("video")}
          type="button"
        >
          Videos <span>{counts.video}</span>
        </button>
        <button
          className={`ag-filter ${filter === "audio" ? "active" : ""}`}
          onClick={() => setFilter("audio")}
          type="button"
        >
          Audio <span>{counts.audio}</span>
        </button>
      </div>

      {filtered.length > 0 ? (
        <div className="ag-grid">
          {filtered.map((item, index) => (
            <motion.button
              key={index}
              className="ag-card"
              type="button"
              onClick={() => handleShow(item)}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.2 }}
            >
              {item.extension_type === "image" && (
                <img
                  src={(item.file_url || "").replace("/thumb", "")}
                  alt="Media"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              {item.extension_type === "video" && (
                <div className="ag-video">
                  <video src={item.file_url} muted />
                  <span className="ag-play">
                    <i className="bx bx-play"></i>
                  </span>
                </div>
              )}
              {item.extension_type === "audio" && (
                <div className="ag-audio">
                  <i className="bx bx-music"></i>
                  <span>Audio</span>
                </div>
              )}
            </motion.button>
          ))}
        </div>
      ) : (
        <div className="ag-empty">
          <i className="bx bx-photo-album"></i>
          <p>No media found in this filter.</p>
        </div>
      )}

      {/* Modal */}
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Media Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {modalMedia?.extension_type === "image" && (
            <img
              src={modalMedia.file_url.replace("/thumb", "")}
              alt="Modal"
              className="img-fluid w-100"
            />
          )}
          {modalMedia?.extension_type === "video" && (
            <video src={modalMedia.file_url} controls className="img-fluid w-100" />
          )}
          {modalMedia?.extension_type === "audio" && (
            <audio controls className="w-100">
              <source src={modalMedia.file_url} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          )}
        </Modal.Body>
      </Modal>
    </motion.div>
  );
};

export default AlbumGallery;
